import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { TableColumn } from 'react-data-table-component';
import { EmptyResult } from '../../../../../_metronic/helpers/components/EmptyResult';
import {
  encryptId,
  formatPrice,
  KTSVG,
} from '../../../../../_metronic/helpers';
import {
  colorStatusCuentasCobrar,
  colorStatusPedidos,
} from '../../../../../_metronic/helpers/CustomElements';

type Props = {
  listado: any;
  cargando: boolean;
};

const ListadoTabla: React.FC<Props> = ({ listado, cargando }) => {
  const [data, setData] = useState<any>([]);
  useEffect(() => {
    if (listado) {
      setData(listado);
    }
    return () => {
      setData([]);
    };
  }, [listado]);

  const columns: TableColumn<any>[] = [
    {
      name: '',
      selector: (row: any) => row.nombre,
      cell: (row: any) => (
        <>
          <span
            className="badge fs-7 fw-bold mr-15 min-w-150px"
            style={{
              color: '#fff',
              backgroundColor: row.color ? row.color : '#9096EC',
            }}
          >
            {row.nombre}
          </span>
          <div
            className="badge badge-light-primary fs-7 fw-bold mr-15"
            title="Total de tareas"
          >
            {row.items.length}
          </div>

          <div className="d-inline-flex gap-2">
            <span
              className="badge badge-light-success fs-7 fw-bold"
              title="Total de tareas"
            >
              {`${formatPrice(row.monto_total_mxn, 'es-MX', 'MXN', 2, 2)}`} MXN
            </span>
            <span
              className="badge badge-light-info fs-7 fw-bold"
              title="Total de tareas"
            >
              {`${formatPrice(row.monto_total_usd, 'es-MX', 'MXN', 2, 2)}`} USD
            </span>
          </div>
        </>
      ),
      sortable: false,
      width: '250px',
    },
  ];

  return (
    <>
      {cargando ? (
        <>
          <EmptyResult title="" loading={cargando} />
        </>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data}
            responsive={true}
            expandableRows
            expandableRowsComponent={expandedComponent}
            noDataComponent={
              <>
                <div className="d-flex align-items-center">
                  <div className="text-muted text-center fs-6 w-100 m-5">
                    Sin registros
                  </div>
                </div>
              </>
            }
            onRowExpandToggled={(event) => {}}
          />
        </>
      )}
    </>
  );
};

const expandedComponent = (d: any) => {
  const columns_section: any = [
    {
      id: 'fecha',
      name: <div className="fw-bolder text-muted me-1">Fecha</div>,
      selector: (row: any) =>
        new Date(row.createdAt).toISOString().split('T')[0],
      cell: (row: any) => (
        <>
          {row.createdAt && (
            <>
              <div className="text-gray-700">
                {new Date(row.createdAt).toISOString().split('T')[0]}
              </div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'folio',
      name: <div className="fw-bolder text-muted me-1">Folio</div>,
      selector: (row: any) => row.folio,
      cell: (row: any) => (
        <>
          {row.folio && (
            <>
              <div className="text-gray-700 fw-bolder">{row.folio}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'tipo_pedido',
      name: <div className="fw-bolder text-muted me-1">Tipo pedido</div>,
      selector: (row: any) => row.tipo.name,
      cell: (row: any) => (
        <>
          {row.tipo && (
            <>
              <div className="text-gray-700">{row.tipo.name}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'nombre',
      name: <div className="fw-bolder text-muted me-1">Nombre</div>,
      selector: (row: any) => row.nombre,
      cell: (row: any) => (
        <>
          {row.nombre && (
            <>
              <div className="text-gray-700">{row.nombre}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'tipo_cotizacion',
      name: <div className="fw-bolder text-muted me-1">Tipo cotizaci&oacute;n</div>,
      selector: (row: any) => row.tipoCotizacion?.name,
      cell: (row: any) => (
        <>
          {row.tipoCotizacion?.name && (
            <>
              <div className="text-gray-700">{row.tipoCotizacion.name}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'cuenta',
      name: <div className="fw-bolder text-muted me-1">Cuenta</div>,
      selector: (row: any) => row.cuenta.nombre,
      cell: (row: any) => (
        <>
          {row.cuenta && (
            <>
              <div className="text-gray-700">{row.cuenta.nombre}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'unidad_negocio',
      name: <div className="fw-bolder text-muted me-1">Unidad de negocio</div>,
      selector: (row: any) =>
        row.unidadNegocio ? row.unidadNegocio.nombre : '',
      cell: (row: any) => (
        <>
          {row.unidadNegocio && (
            <>
              <div className="text-gray-700">{row.unidadNegocio.nombre}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'monto',
      name: <div className="fw-bolder text-muted me-1">Monto c/imp</div>,
      selector: (row: any) => Number(row.monto_c_imp),
      cell: (row: any) => (
        <div>
          <span className="text-gray-700 fw-bolder">
            {`${formatPrice(row.monto_c_imp, 'es-MX', 'MXN', 2, 2)}`}
          </span>
          <span className="text-gray-600 ms-3 fs-8">
            {row.unidadMoneda
              ? `/ ${row.unidadMoneda.code}`
              : 'Sin unidad de moneda'}
          </span>
        </div>
      ),
      sortable: true,
    },
    {
      id: 'comprador',
      name: <div className="fw-bolder text-muted me-1">Comprador</div>,
      selector: (row: any) => row.compradorAsignado ? row.compradorAsignado.first_name : '',
      cell: (row: any) => (
        <div className="text-gray-700 fw-bolder">
          {row.compradorAsignado?.first_name} {row.compradorAsignado?.father_last_name}
        </div>
      ),
      sortable: true,
      width: '110px'
    },
    {
      id: 'estatus',
      name: <div className="fw-bolder text-muted me-1">Estatus</div>,
      selector: (row: any) => (row.status ? row.status.name : 'No registrado'),
      cell: (row: any) => (
        <div
          className={`${row.status ? colorStatusPedidos(row.status.code) : ''}`}
        >
          {row.status ? row.status.name : 'N/A'}{' '}
        </div>
      ),
      sortable: true,
      center: true,
    },
  ];

  return (
    <>
      <DataTable
        className="mb-5"
        columns={columns_section}
        data={d.data.items}
        customStyles={{
          table: {
            style: {
              minWidth: '110%',
              width: '900px',
              
            },
          },
        }}
        responsive
        noDataComponent={
          <>
            <div className="d-flex align-items-center ">
              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-md-12 collg-12 text-center wh-0100">
                  <div className="text-muted fs-6 w-100 m-5">Sin registros</div>
                </div>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

export { ListadoTabla };
